import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';

import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { ConsolidatedSkill } from '@/services/workersV2API';

type Props = {
  skill: ConsolidatedSkill;
  workerId: number;
  onUpdate: () => void;
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '--';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDateUTC = (dateString: string | null | undefined): string => {
  if (!dateString) return '--';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const BooleanBadge = ({ value }: { value: boolean }) => {
  return value ? (
    <Badge label="Yes" status="theme" />
  ) : (
    <Badge label="No" status="danger" />
  );
};

const KeyValueRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <TableRow>
    <TableCell
      component="th"
      scope="row"
      sx={{
        fontWeight: 600,
        width: '40%',
        color: '#6B7280',
        fontSize: '13px',
        borderBottom: '1px solid #F3F4F6',
        padding: '10px 16px',
      }}
    >
      {label}
    </TableCell>
    <TableCell
      sx={{
        fontSize: '13px',
        borderBottom: '1px solid #F3F4F6',
        padding: '10px 16px',
      }}
    >
      {children}
    </TableCell>
  </TableRow>
);

const KeyValueItem = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <Small color="lighter" style={{ marginBottom: '4px' }}>
      {label}
    </Small>
    <div style={{ fontSize: '13px' }}>{children}</div>
  </div>
);

const SkillOverviewTab = ({ skill, workerId, onUpdate }: Props) => {
  const navigate = useNavigate();
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [expiryDialog, setExpiryDialog] = useState<{
    open: boolean;
    value: string;
  }>({
    open: false,
    value: '',
  });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    markAsIneligible: boolean;
  }>({
    open: false,
    markAsIneligible: true,
  });

  const workerSkill = skill.worker_skill;
  const skillRequest = skill.skill_request;

  const handleReject = async () => {
    if (!skillRequest) return;
    setLoadingAction('reject');
    try {
      const response = await axiosRubyClient.patch(
        `/api/admin/skill_requests/${skillRequest.id}/reject`,
        { rejected: true },
      );
      if (response.data.success) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error rejecting skill request:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleUnreject = async () => {
    if (!skillRequest) return;
    setLoadingAction('unreject');
    try {
      const response = await axiosRubyClient.patch(
        `/api/admin/skill_requests/${skillRequest.id}/reject`,
        { rejected: false },
      );
      if (response.data.success) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error unrejecting skill request:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleAccept = async () => {
    if (!skillRequest) return;
    setLoadingAction('accept');
    try {
      const response = await axiosRubyClient.patch(
        `/api/admin/skill_requests/${skillRequest.id}/accept`,
      );
      if (response.data.success) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error accepting skill request:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleToggle = async () => {
    if (!workerSkill) return;
    setLoadingAction('toggle');
    try {
      const response = await axiosRubyClient.post(
        `/api/admin/worker_skills/${workerSkill.id}/toggle`,
      );
      if (response.data.success) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error toggling worker skill:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleSetExpiry = async () => {
    if (!workerSkill || !expiryDialog.value) return;
    setLoadingAction('expiry');
    try {
      const response = await axiosRubyClient.patch(
        `/api/admin/worker_skills/${workerSkill.id}`,
        { expires_at: `${expiryDialog.value}T14:00:00Z` },
      );
      if (response.data.success) {
        setExpiryDialog({ open: false, value: '' });
        onUpdate();
      }
    } catch (error) {
      console.error('Error setting expiry:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDeleteSkill = async () => {
    if (!workerSkill) return;
    setLoadingAction('delete');
    setDeleteError(null);
    try {
      const response = await axiosRubyClient.delete(
        `/api/admin/worker_skills/${workerSkill.id}`,
        {
          data: {
            mark_as_ineligible: deleteDialog.markAsIneligible,
          },
        },
      );
      if (response.data.success) {
        setDeleteDialog({ open: false, markAsIneligible: true });
        navigate(`/workers_v2/${workerId}`);
      } else {
        setDeleteError(response.data.message || 'Failed to remove skill');
      }
    } catch (error: any) {
      console.error('Error removing skill:', error);
      setDeleteError(
        error.response?.data?.message ||
          error.response?.data?.errors?.join(', ') ||
          'Failed to remove skill. The worker may have active shifts with this skill.',
      );
    } finally {
      setLoadingAction(null);
    }
  };

  const handleCreateWorkerSkill = async () => {
    setLoadingAction('create_ws');
    try {
      const response = await axiosRubyClient.post(
        `/api/admin/workers/${workerId}/worker_skills`,
        { skill_id: skill.skill_id },
      );
      if (response.data.success) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error creating worker skill:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  const getSkillRequestStatusBadge = () => {
    if (!skillRequest) return null;
    if (skillRequest.accepted_at)
      return <Badge label="Accepted" status="theme" />;
    if (skillRequest.rejected_at)
      return <Badge label="Rejected" status="danger" />;
    return <Badge label="Pending" status="warning" />;
  };

  const isRejected = skillRequest?.rejected_at != null;
  const isAccepted = skillRequest?.accepted_at != null;
  const isPending = skillRequest != null && !isAccepted && !isRejected;

  return (
    <Stack vertical gap={24} style={{ width: '100%' }}>
      {/* Record Status */}
      <Card>
        <Card.Section>
          <Stack gap={16} wrap>
            <Stack gap={8} style={{ alignItems: 'center' }}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: workerSkill ? '#10B981' : '#EF4444',
                }}
              />
              <Small>
                Worker Skill:{' '}
                {workerSkill ? `Exists (ID: ${workerSkill.id})` : 'Not created'}
              </Small>
            </Stack>
            <Stack gap={8} style={{ alignItems: 'center' }}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: skillRequest ? '#10B981' : '#9CA3AF',
                }}
              />
              <Small>
                Skill Request:{' '}
                {skillRequest ? `Exists (ID: ${skillRequest.id})` : 'None'}
              </Small>
            </Stack>
          </Stack>
        </Card.Section>
      </Card>

      {/* Worker Skill */}
      <Card
        title="Worker Skill"
        actionsDropdown={
          !workerSkill ? (
            <Stack gap={8}>
              <Button
                variant="contained"
                size="small"
                disabled={loadingAction !== null}
                onClick={handleCreateWorkerSkill}
                startIcon={
                  loadingAction === 'create_ws' ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
              >
                {loadingAction === 'create_ws'
                  ? 'Creating...'
                  : 'Create Worker Skill'}
              </Button>
            </Stack>
          ) : undefined
        }
      >
        <Card.Section>
          {workerSkill ? (
            <>
              {workerSkill.disabled && (
                <Stack
                  gap={12}
                  style={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    marginBottom: '12px',
                    backgroundColor: '#FEF2F2',
                    borderRadius: '8px',
                    border: '1px solid #FECACA',
                  }}
                >
                  <Stack gap={8} style={{ alignItems: 'center' }}>
                    <Badge label="Disabled" status="danger" />
                    <Small>This worker skill is currently disabled.</Small>
                  </Stack>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    disabled={loadingAction !== null}
                    onClick={handleToggle}
                    startIcon={
                      loadingAction === 'toggle' ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : undefined
                    }
                  >
                    {loadingAction === 'toggle' ? 'Enabling...' : 'Enable'}
                  </Button>
                </Stack>
              )}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '16px',
                  padding: '4px 0',
                }}
              >
                <KeyValueItem label="Shifts Worked">
                  {workerSkill.jobs_worked}
                </KeyValueItem>
                <KeyValueItem label="Avg Rating">
                  {workerSkill.jobs_worked > 0
                    ? (
                        workerSkill.job_rating_sum / workerSkill.jobs_worked
                      ).toFixed(1)
                    : '--'}
                </KeyValueItem>
                <KeyValueItem label="Shifts Count">
                  {workerSkill.jobs_count}
                </KeyValueItem>
                <KeyValueItem label="ID">{workerSkill.id}</KeyValueItem>
                <KeyValueItem label="Expires At">
                  {formatDateUTC(workerSkill.expires_at)}
                </KeyValueItem>
                <KeyValueItem label="Days Until Expiration">
                  {workerSkill.days_until_expiration != null
                    ? workerSkill.days_until_expiration
                    : '--'}
                </KeyValueItem>
                <KeyValueItem label="Renewal Notified At">
                  {formatDate(workerSkill.renewal_notified_at)}
                </KeyValueItem>
                <KeyValueItem label="Created At">
                  {formatDate(workerSkill.created_at)}
                </KeyValueItem>
                <KeyValueItem label="Updated At">
                  {formatDate(workerSkill.updated_at)}
                </KeyValueItem>
              </div>

              {/* Manage Skill Actions */}
              <Divider sx={{ my: 2 }} />
              <Body weight="semibold" style={{ marginBottom: '12px' }}>
                Manage Skill
              </Body>
              <Stack vertical gap={12}>
                {/* Disable / Enable */}
                <Stack
                  style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '8px',
                  }}
                >
                  <Stack vertical gap={2}>
                    <Small weight="semibold">
                      {workerSkill.disabled ? 'Enable Skill' : 'Disable Skill'}
                    </Small>
                    <Small color="lighter">
                      {workerSkill.disabled
                        ? 'Re-enable this skill so the worker can be assigned to jobs again.'
                        : 'Temporarily prevent this worker from being assigned to jobs with this skill. Can be re-enabled.'}
                    </Small>
                  </Stack>
                  <Button
                    variant="outlined"
                    size="small"
                    color={workerSkill.disabled ? 'success' : 'warning'}
                    disabled={loadingAction !== null}
                    onClick={handleToggle}
                    startIcon={
                      loadingAction === 'toggle' ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : undefined
                    }
                    sx={{ minWidth: '100px', flexShrink: 0 }}
                  >
                    {loadingAction === 'toggle'
                      ? workerSkill.disabled
                        ? 'Enabling...'
                        : 'Disabling...'
                      : workerSkill.disabled
                        ? 'Enable'
                        : 'Disable'}
                  </Button>
                </Stack>

                {/* Set Expiry */}
                <Stack
                  style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '8px',
                  }}
                >
                  <Stack vertical gap={2}>
                    <Small weight="semibold">Set Expiry</Small>
                    <Small color="lighter">
                      Set when this skill certification expires. Worker will be
                      notified before expiration.
                    </Small>
                  </Stack>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={loadingAction !== null}
                    onClick={() =>
                      setExpiryDialog({
                        open: true,
                        value: workerSkill.expires_at
                          ? workerSkill.expires_at.split('T')[0]
                          : '',
                      })
                    }
                    sx={{ minWidth: '100px', flexShrink: 0 }}
                  >
                    Set Expiry
                  </Button>
                </Stack>

                {/* Remove */}
                <Stack
                  style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    backgroundColor: '#FEF2F2',
                    borderRadius: '8px',
                    border: '1px solid #FECACA',
                  }}
                >
                  <Stack vertical gap={2}>
                    <Small weight="semibold">Remove Skill</Small>
                    <Small color="lighter">
                      Permanently remove this skill from the worker. This cannot
                      be undone.
                    </Small>
                  </Stack>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    disabled={loadingAction !== null}
                    onClick={() =>
                      setDeleteDialog({ open: true, markAsIneligible: true })
                    }
                    sx={{ minWidth: '100px', flexShrink: 0 }}
                  >
                    Remove
                  </Button>
                </Stack>
              </Stack>
            </>
          ) : (
            <Small color="lighter">No worker skill record exists yet.</Small>
          )}
        </Card.Section>
      </Card>

      {/* Skill Request */}
      {skillRequest && (
        <Card
          title="Skill Request"
          actionsDropdown={
            <Stack gap={8}>
              {isPending && (
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  disabled={loadingAction !== null}
                  onClick={handleAccept}
                  startIcon={
                    loadingAction === 'accept' ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {loadingAction === 'accept' ? 'Approving...' : 'Approve'}
                </Button>
              )}
              {!isRejected && (
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  disabled={loadingAction !== null}
                  onClick={handleReject}
                  startIcon={
                    loadingAction === 'reject' ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {loadingAction === 'reject' ? 'Rejecting...' : 'Reject'}
                </Button>
              )}
              {isRejected && (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={loadingAction !== null}
                  onClick={handleUnreject}
                  startIcon={
                    loadingAction === 'unreject' ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {loadingAction === 'unreject' ? 'Unrejecting...' : 'Unreject'}
                </Button>
              )}
            </Stack>
          }
        >
          <Card.Section>
            <Table size="small">
              <TableBody>
                <KeyValueRow label="ID">{skillRequest.id}</KeyValueRow>
                <KeyValueRow label="Status">
                  {getSkillRequestStatusBadge()}
                </KeyValueRow>
                <KeyValueRow label="Work Experience Completed">
                  <Stack gap={8} style={{ alignItems: 'center' }}>
                    <BooleanBadge
                      value={skillRequest.work_experience_completed}
                    />
                    {skillRequest.work_experience_completed_at && (
                      <Small color="lighter">
                        {formatDate(skillRequest.work_experience_completed_at)}
                      </Small>
                    )}
                  </Stack>
                </KeyValueRow>
                <KeyValueRow label="Assessment Passed">
                  <Stack gap={8} style={{ alignItems: 'center' }}>
                    <BooleanBadge value={skillRequest.assessment_passed} />
                    {skillRequest.assessment_passed_at && (
                      <Small color="lighter">
                        {formatDate(skillRequest.assessment_passed_at)}
                      </Small>
                    )}
                  </Stack>
                </KeyValueRow>
                <KeyValueRow label="Uniform Photo Uploaded">
                  <Stack gap={8} style={{ alignItems: 'center' }}>
                    <BooleanBadge value={skillRequest.uniform_photo_uploaded} />
                    {skillRequest.uniform_photo_uploaded_at && (
                      <Small color="lighter">
                        {formatDate(skillRequest.uniform_photo_uploaded_at)}
                      </Small>
                    )}
                  </Stack>
                </KeyValueRow>
                <KeyValueRow label="Certificate Photo Uploaded">
                  <Stack gap={8} style={{ alignItems: 'center' }}>
                    <BooleanBadge
                      value={skillRequest.certificate_photo_uploaded}
                    />
                    {skillRequest.certificate_photo_uploaded_at && (
                      <Small color="lighter">
                        {formatDate(skillRequest.certificate_photo_uploaded_at)}
                      </Small>
                    )}
                  </Stack>
                </KeyValueRow>
                <KeyValueRow label="Accepted At">
                  {formatDate(skillRequest.accepted_at)}
                </KeyValueRow>
                <KeyValueRow label="Rejected At">
                  {formatDate(skillRequest.rejected_at)}
                </KeyValueRow>
                <KeyValueRow label="Rejection Comment">
                  {skillRequest.rejection_comment || '--'}
                </KeyValueRow>
                <KeyValueRow label="Assessment Attempt Count">
                  {skillRequest.assessment_attempt_count}
                </KeyValueRow>
                <KeyValueRow label="Assessment Permanently Ineligible">
                  <BooleanBadge
                    value={skillRequest.assessment_permanently_ineligible}
                  />
                </KeyValueRow>
                <KeyValueRow label="Can Retry Assessment">
                  <BooleanBadge value={skillRequest.can_retry_assessment} />
                </KeyValueRow>
                <KeyValueRow label="Retry Eligible At">
                  {formatDate(skillRequest.assessment_retry_eligible_at)}
                </KeyValueRow>
                <KeyValueRow label="Retry Message">
                  {skillRequest.retry_message || '--'}
                </KeyValueRow>
                <KeyValueRow label="All Requirements Completed">
                  <BooleanBadge
                    value={skillRequest.all_requirements_completed}
                  />
                </KeyValueRow>
                <KeyValueRow label="Completion Percentage">
                  {skillRequest.completion_percentage}%
                </KeyValueRow>
                <KeyValueRow label="Created At">
                  {formatDate(skill.created_at)}
                </KeyValueRow>
              </TableBody>
            </Table>
          </Card.Section>
        </Card>
      )}

      {/* Set Expiry Dialog */}
      <Dialog
        open={expiryDialog.open}
        onClose={() => setExpiryDialog({ open: false, value: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Set Expiry Date</DialogTitle>
        <DialogContent>
          <TextField
            type="date"
            fullWidth
            value={expiryDialog.value}
            onChange={(e) =>
              setExpiryDialog({ ...expiryDialog, value: e.target.value })
            }
            sx={{ marginTop: '8px' }}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpiryDialog({ open: false, value: '' })}>
            Cancel
          </Button>
          <Button
            onClick={handleSetExpiry}
            variant="contained"
            color="primary"
            disabled={!expiryDialog.value || loadingAction === 'expiry'}
            startIcon={
              loadingAction === 'expiry' ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {loadingAction === 'expiry' ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Skill Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ ...deleteDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove Skill from Worker</DialogTitle>
        <DialogContent>
          <Small style={{ marginBottom: '16px', display: 'block' }}>
            Are you sure you want to remove "{skill.skill_name}" from this
            worker? This action cannot be undone.
          </Small>
          <FormControlLabel
            control={
              <Checkbox
                checked={deleteDialog.markAsIneligible}
                onChange={(e) =>
                  setDeleteDialog({
                    ...deleteDialog,
                    markAsIneligible: e.target.checked,
                  })
                }
                color="primary"
              />
            }
            label="Also mark as ineligible for this skill"
          />
          {deleteError && (
            <Small
              color="danger"
              style={{ display: 'block', marginTop: '12px' }}
            >
              {deleteError}
            </Small>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ ...deleteDialog, open: false })}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSkill}
            variant="contained"
            color="error"
            disabled={loadingAction === 'delete'}
            startIcon={
              loadingAction === 'delete' ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {loadingAction === 'delete' ? 'Removing...' : 'Remove Skill'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SkillOverviewTab;
